import { Duration, Instant, convert } from "@js-joda/core";
import { i18n } from "next-i18next";

export type DateTimeFormat =
  | "e, MMM D, hh:mm tt"
  | "E, MMMM D, YYYY"
  | "MMMM D, YYYY"
  | "MM/dd/yyyy"
  | "MM/dd/yyyy hh:mm tt"
  | "MMM D, YYYY, HH:mm:ss"
  | "MMM D, YYYY, hh:mm:ss tt"
  | "MMM D, YYYY"
  | "MMMM, YYYY"
  | "MMM, YYYY"
  | "h:mm aa";

type TDateTimeFormat = {
  [F in DateTimeFormat]: Intl.DateTimeFormatOptions;
};

export const getHoursFormatted = (time: string, locale: string) => {
  const parts = time.split(":") as unknown as number[];
  if (parts.length < 2) throw Error("Invalid time format");

  const date = new Date();
  date.setHours(Number(parts[0]), Number(parts[1]), 0, 0);

  const timeFormat: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  return new Intl.DateTimeFormat(locale, timeFormat).format(date);
};

export const getDateFormatted = (date: string, locale: string) => {
  const objDate = new Date(date);
  if (objDate.toString() === "Invalid Date") throw Error("Invalid date format");

  const dayFormat: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  return new Intl.DateTimeFormat(locale, dayFormat).format(objDate);
};

export const DateTimeFormatOptions: TDateTimeFormat = {
  "e, MMM D, hh:mm tt": {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
  "E, MMMM D, YYYY": {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  "MMMM D, YYYY": {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  "MM/dd/yyyy": {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  },
  "MM/dd/yyyy hh:mm tt": {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
  "MMM D, YYYY, HH:mm:ss": {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
  },
  "MMM D, YYYY, hh:mm:ss tt": {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: true,
  },
  "MMM D, YYYY": {
    dateStyle: "medium",
  },
  "MMMM, YYYY": {
    year: "numeric",
    month: "long",
  },
  "MMM, YYYY": {
    year: "numeric",
    month: "short",
  },
  "h:mm aa": {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
};

export const getFormattedInstant = (instant: Instant) => {
  const date = convert(instant).toDate();
  const year = date.getFullYear();
  const month = (1 + date.getMonth()).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  return `${month}/${day}/${year}`;
};

export const dateTimeFormatter = (
  date: Date | null,
  locale: string,
  format: DateTimeFormat
) => {
  if (!date) return "";

  return new Intl.DateTimeFormat(locale, DateTimeFormatOptions[format]).format(
    date
  );
};

export const formatTimeOnly = (date: Date, locale: string) => {
  return date.toLocaleString(locale, {
    hour: "numeric",
    minute: "numeric",
    hour12: locale === "en-CA" || locale === "en",
  });
};

export const getTimeAgo = (date: Instant | string): string => {
  const now = Instant.now();
  const instant = typeof date === "string" ? Instant.parse(date) : date;
  const duration = Duration.between(instant, now);

  if (duration.toMinutes() < 5) {
    return i18n?.t("common-components:just_now") ?? "Just now";
  }

  if (duration.toMinutes() < 60) {
    const minutes = Math.floor(duration.toMinutes());
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  }

  if (duration.toHours() < 24) {
    const hours = Math.floor(duration.toHours());
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }

  const days = Math.floor(duration.toDays());
  return `${days} day${days !== 1 ? "s" : ""} ago`;
};

export const getDaysDifference = (
  from: Date,
  to: Date,
  allowNegatives = true
): number => {
  let diffTime = to.getTime() - from.getTime();
  if (!allowNegatives && diffTime < 0) {
    diffTime = Math.abs(diffTime);
  }
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};
